/* eslint-disable no-restricted-imports */
import React, {useRef, useState, useEffect, useCallback} from 'react'
import {UIGameUserStatesProvider} from '../contexts/UIGameUserStatesContext'
import {UIGameStateProvider} from '../contexts/UIGameStateContext'
import {UIGameFeeds} from './UIGameFeeds/UIGameFeeds'
import {GameState, NestedReadonly} from '@colonist/game-states'
import {GameClientUserState, GameSettings} from '@colonist/game-base-types'
import {UIGameManager} from '../../../../code/client/shared/scripts/ui_game/UIGameManager'
import UIGameSettingsProvider from '../contexts/UIGameSettingsContext'
import {UIGameManagerProvider} from '../contexts/UIGameManagerContext'
import {UIGameSettings} from './UIGameSettings'
import {UIUtilsEjsVariables} from '../../../../code/client/shared/scripts/UIUtilsEjsVariables'

// eslint-disable-next-line @typescript-eslint/no-empty-function
const globalGameStateSetter = {setGameState: (gameState: GameState) => {}}

export function setGlobalGameState(gameState: NestedReadonly<GameState>) {
    const gameStateNormal = gameState as GameState // all React states are mutable, no need for NestedReadonly
    globalGameStateSetter.setGameState({...gameStateNormal})
}

let globalResizeUIGame: undefined | VoidFunction
export function resizeUIGame() {
    if(globalResizeUIGame) globalResizeUIGame()
}

export interface UIGameOptions {
    isChatEnabled: boolean
}

export default function UIGame({
    gameManager,
    initialUserStates,
    initialGameState,
    options,
    gameSettings,
}: {
    gameManager: UIGameManager
    initialUserStates: GameClientUserState[]
    initialGameState: GameState
    options: UIGameOptions
    gameSettings: GameSettings
}) {
    const gameSettingsContainerRef = useRef<HTMLDivElement>(null)
    const gameFeedsContainerRef = useRef<HTMLDivElement>(null)
    const [userStates, setUserStates] = useState(initialUserStates)
    const [gameState, setGameState] = useState(initialGameState)
    const isChatDefaultMinimized = gameManager.gameController.isBotGame()

    globalGameStateSetter.setGameState = setGameState

    const onClickLog = useCallback((logIndex: number) => {
        gameManager.replayView?.replayController.clickedGameLogWithIndex(logIndex)
    }, [gameManager])

    const onSendChat = (message: string) => {
        gameManager.socketGameSend.submitChat(message)
    }

    const resize = () => {
        const canvas = gameManager.canvasController
        const gameContainer = document.getElementById('ui-game') as HTMLDivElement | null
        const view = canvas.app.view
        // TODO remove this when everything migrated to React and use CSS
        if(gameContainer) {
            gameContainer.style.top = view.offsetTop - view.clientHeight / 2 + 'px'
            gameContainer.style.left = canvas.app.view.style.left + 'px'
            gameContainer.style.width = canvas.appW + 'px'
            gameContainer.style.height = canvas.appH + 'px'
        }
        if(gameFeedsContainerRef.current) {
            const container = gameFeedsContainerRef.current
            const height = canvas.feedsHeight
            container.style.top = canvas.margin + 'px'
            container.style.right = canvas.margin + 'px'
            container.style.width = canvas.chatWidth - canvas.margin * 2 + 'px'
            container.style.height = height - canvas.margin * 2 + 'px'
        }
        if(gameSettingsContainerRef.current) {
            const container = gameSettingsContainerRef.current
            container.style.left = canvas.app.view.style.left + 'px'
            container.style.width = (canvas.appW - canvas.chatWidth) + 'px'
            container.style.height = canvas.appH + 'px'
        }
        gameManager.replayView?.setPosition()
    }

    globalResizeUIGame = resize

    useEffect(() => {
        if(!UIUtilsEjsVariables.isUserOnMobileApp) {
            window.addEventListener('resize', resize)
        }
        resize()

        return () => {
            if(!UIUtilsEjsVariables.isUserOnMobileApp) {
                window.removeEventListener('resize', resize)
            }
        }
    }, [])

    return (
        <UIGameUserStatesProvider userStates={userStates}>
            <UIGameManagerProvider gameManager={gameManager}>
                <UIGameStateProvider gameState={gameState}>
                    <UIGameSettingsProvider gameSettings={gameSettings}>
                        <UIGameFeeds
                            containerRef={gameFeedsContainerRef}
                            isChatDefaultMinimized={isChatDefaultMinimized}
                            isChatEnabled={options.isChatEnabled}
                            onClickLog={onClickLog}
                            onSendChat={onSendChat}/>
                        <UIGameSettings containerRef={gameSettingsContainerRef}/>
                    </UIGameSettingsProvider>
                </UIGameStateProvider>
            </UIGameManagerProvider>
        </UIGameUserStatesProvider>
    )
}
